// audioManager.js
import * as THREE from 'three';

class AudioManager {
    static instance = null;

    audioLoader = new THREE.AudioLoader();
    sounds = {};
    audioListener = null;

    isSoundEnabled = true;
    globalVolume = 0.5;
    musicVolume = 0.5;
    sfxVolume = 0.5;
    musicEnabled = true;
    sfxEnabled = true;

    loopedSounds = new Map();

    _settingsUnsubscribe = null;

    constructor() {
        if (AudioManager.instance) {
            return AudioManager.instance;
        }
        AudioManager.instance = this;
        console.log("AudioManager singleton instance created.");
        document.body.addEventListener('click', this.resumeAudioContext);
        document.body.addEventListener('touchstart', this.resumeAudioContext);
        document.body.addEventListener('keydown', this.resumeAudioContext);
    }

    static getInstance() {
        if (!AudioManager.instance) {
            AudioManager.instance = new AudioManager();
        }
        return AudioManager.instance;
    }

    initListener(camera) {
        if (this.audioListener) {
            if (this.audioListener.parent !== camera && this.audioListener.context.state !== 'closed') {
                this.audioListener.context.suspend().then(() => {
                    if (this.audioListener.parent) {
                        this.audioListener.parent.remove(this.audioListener);
                    }
                    camera.add(this.audioListener);
                    this.audioListener.context.resume();
                }).catch(e => console.error("initListener: Error re-parenting audio context:", e));
            } else if (this.audioListener.context.state === 'closed') {
                console.warn("initListener: Audio context is closed, cannot re-parent listener.");
            } else {
                console.log("initListener: Listener already initialized and attached to this camera.");
            }
            return;
        }
        this.audioListener = new THREE.AudioListener();
        camera.add(this.audioListener);
        this.resumeAudioContext();
    }

    initSettings(settingsStore) {
        if (this._settingsUnsubscribe) {
            console.warn("initSettings: AudioManager already subscribed to settings.");
            return;
        }
        this._settingsUnsubscribe = settingsStore.onChange(this.onSettingsChange.bind(this));
    }

    disposeSettings() {
        if (this._settingsUnsubscribe) {
            this._settingsUnsubscribe();
            this._settingsUnsubscribe = null;
        }
    }

    onSettingsChange(settings) {
        const previousSoundEnabled = this.isSoundEnabled;
        const previousVolume = this.globalVolume;

        const previousMusicVolume = this.musicVolume;
        const previousSfxVolume = this.sfxVolume;

        this.isSoundEnabled = settings.play_sound ?? true;
        this.globalVolume = Math.max(0.0, Math.min(1.0, settings.volume ?? 0.5)); // Master volume
        this.musicVolume = Math.max(0.0, Math.min(1.0, settings.musicVolume ?? 0.5));
        this.sfxVolume = Math.max(0.0, Math.min(1.0, settings.sfxVolume ?? 0.5));
        this.musicEnabled = settings.musicEnabled ?? true;
        this.sfxEnabled = settings.sfxEnabled ?? true;


        if (previousSoundEnabled !== this.isSoundEnabled) {
            if (this.isSoundEnabled) {
                // console.log("onSettingsChange: Sound enabled. Attempting to resume context and looped sounds.");
                this.resumeAudioContext().then(() => {
                    this.loopedSounds.forEach((sound, name) => {
                        //   console.log(`onSettingsChange: Checking looped sound "${name}". isPlaying: ${sound.isPlaying}, getLoop(): ${sound.getLoop()}`);
                        if (!sound.isPlaying && sound.getLoop()) {
                            //   console.log(`onSettingsChange: Resuming looped sound: ${name}`);
                            // Adjust volume relative to previous volume before playing
                            const volumeRatio = this.globalVolume / (previousVolume || 1);
                            sound.setVolume(sound.getVolume() * volumeRatio);
                            sound.play();
                        } else if (sound.isPlaying && sound.getLoop()) {
                            console.log(`onSettingsChange: Looped sound "${name}" is already playing.`);
                        } else if (!sound.getLoop()) {
                            console.warn(`onSettingsChange: Sound "${name}" in loopedSounds map is not set to loop.`);
                        }
                    });
                }).catch(e => console.error("onSettingsChange: Error resuming context after enabling sound:", e));

            } else {
                //  console.log("onSettingsChange: Sound disabled. Stopping all looped sounds.");
                this.loopedSounds.forEach((sound, name) => {
                    if (sound.isPlaying) {
                        console.log(`onSettingsChange: Stopping looped sound: ${name}`);
                        sound.stop();
                    } else {
                        console.log(`onSettingsChange: Looped sound "${name}" is already stopped.`);
                    }
                });
            }
        }

        // Update volumes for playing sounds if settings changed
        if (this.isSoundEnabled) {
            this.loopedSounds.forEach((sound, name) => {
                const isMusic = name.includes('music') || name.includes('Music');
                const typeVolume = isMusic ? this.musicVolume : this.sfxVolume;
                const typeEnabled = isMusic ? this.musicEnabled : this.sfxEnabled;

                if (sound.isPlaying) {
                    if (!typeEnabled) {
                        sound.stop();
                    } else {
                        // Re-calculate volume: user input volume (assumed 1 for looped bgm usually) * global * type
                        // We don't track original volume passed to playSound easily unless we store it.
                        // For now assume base volume 1 or maintain relative?
                        // Ideally we store metadata. For now, let's just use current * ratio? No, that drifts.
                        // Let's assume standard BGM matches.
                        sound.setVolume(1 * this.globalVolume * typeVolume);
                    }
                } else if (typeEnabled && sound.getLoop() && isMusic) {
                    // Resume music if re-enabled
                    sound.setVolume(1 * this.globalVolume * typeVolume);
                    sound.play();
                }
            });
        }
    }

    resumeAudioContext = () => {
        if (this.audioListener && this.audioListener.context.state !== 'running' && this.audioListener.context.state !== 'closed') {
            //  console.log("resumeAudioContext: Attempting to resume AudioContext.");
            return this.audioListener.context.resume().then(() => {
                console.log('resumeAudioContext: AudioContext resumed successfully.');
            }).catch(e => console.error('resumeAudioContext: Error resuming AudioContext:', e));
        } else {
            console.log(`resumeAudioContext: No listener or context state is ${this.audioListener?.context.state}. No resume needed.`);
        }
        return Promise.resolve();
    }

    async _loadSound(name, filePath) {
        if (this.sounds[name]) {
            return this.sounds[name];
        }

        try {
            const buffer = await this.audioLoader.loadAsync(filePath);
            this.sounds[name] = buffer;
            // console.log(`_loadSound: Sound loaded: ${name}`);
            return buffer;
        } catch (error) {
            console.error(`_loadSound: Error loading sound ${name}:`, error);
            throw error;
        }
    }

    async loadSounds(soundsToLoad) {
        if (!Array.isArray(soundsToLoad)) {
            //  console.error("loadSounds: expects an array.");
            return;
        }
        //  console.log(`loadSounds: Loading ${soundsToLoad.length} sounds...`);
        try {
            await Promise.all(soundsToLoad.map(s => this._loadSound(s.name, s.filePath)));
            //  console.log("loadSounds: All sounds loaded.");
        } catch (error) {
            console.error("loadSounds: Failed to load one or more sounds:", error);
        }
    }

    playSound(name, volume = 1, loop = false) {
        if (!this.isSoundEnabled || !this.audioListener || this.audioListener.context.state !== 'running') {
            //  console.log(`playSound: Sound "${name}" not played. Enabled: ${this.isSoundEnabled}, Listener: ${!!this.audioListener}, Context State: ${this.audioListener?.context.state}`);
            return null;
        }
        const buffer = this.sounds[name];
        if (!buffer) {
            console.warn(`playSound: Sound "${name}" not loaded yet.`);
            return null;
        }

        const sound = new THREE.Audio(this.audioListener);
        sound.setBuffer(buffer);

        // Determine type based on name or loop property (heuristic for now, or use explicit tag)
        const isMusic = name.includes('music') || name.includes('Music');
        const typeVolume = isMusic ? this.musicVolume : this.sfxVolume;
        const typeEnabled = isMusic ? this.musicEnabled : this.sfxEnabled;

        if (!typeEnabled) {
            // If specific type disabled, don't play.
            return null;
        }

        sound.setVolume(volume * this.globalVolume * typeVolume);
        sound.setLoop(loop);

        if (loop) {
            // console.log(`playSound: Tracking looped sound: ${name}`);
            this.loopedSounds.set(name, sound);
            sound.onEnded = () => {
                //  console.log(`playSound: Looped sound "${name}" onEnded. Loop status: ${sound.getLoop()}`);
                if (!sound.getLoop()) {
                    this.loopedSounds.delete(name);
                    //  console.log(`playSound: Looped sound "${name}" removed from tracking.`);
                }
            };
        }


        // console.log(`playSound: Playing sound "${name}" (Loop: ${loop}, Volume: ${(volume * this.globalVolume).toFixed(4)})`);
        sound.play();

        return sound;
    }

    playPositionalSound(name, position, volume = 1, loop = false, refDistance = 1, rolloffFactor = 1, scene = null) {
        if (!this.isSoundEnabled || !this.audioListener || this.audioListener.context.state !== 'running') {
            //  console.log(`playPositionalSound: Sound "${name}" not played. Enabled: ${this.isSoundEnabled}, Listener: ${!!this.audioListener}, Context State: ${this.audioListener?.context.state}`);
            return null;
        }
        const buffer = this.sounds[name];
        if (!buffer) {
            console.warn(`playPositionalSound: Sound "${name}" not loaded yet.`);
            return null;
        }

        const sound = new THREE.PositionalAudio(this.audioListener);
        const isMusic = name.includes('music');
        const typeVolume = isMusic ? this.musicVolume : this.sfxVolume;
        const typeEnabled = isMusic ? this.musicEnabled : this.sfxEnabled;

        if (!typeEnabled) return null;

        sound.setBuffer(buffer);
        sound.setVolume(volume * this.globalVolume * typeVolume);
        sound.setLoop(loop);
        sound.setRefDistance(refDistance);
        sound.setRolloffFactor(rolloffFactor);

        const soundObject = new THREE.Object3D();
        soundObject.position.copy(position);
        soundObject.add(sound);

        if (scene) {
            scene.add(soundObject);
        } else {
            console.warn(`playPositionalSound: Positional sound "${name}" created but not added to scene. Spatialization may not work correctly.`);
        }

        if (loop) {
            // console.log(`playPositionalSound: Tracking looped positional sound: ${name}`);
            this.loopedSounds.set(name, sound);
            sound.onEnded = () => {
                //  console.log(`playPositionalSound: Looped positional sound "${name}" onEnded. Loop status: ${sound.getLoop()}`);
                if (!sound.getLoop()) {
                    this.loopedSounds.delete(name);
                    //   console.log(`playPositionalSound: Looped positional sound "${name}" removed from tracking.`);
                }
            };
        }

        //  console.log(`playPositionalSound: Playing sound "${name}" at (${position.x.toFixed(1)}, ${position.y.toFixed(1)}, ${position.z.toFixed(1)}) (Loop: ${loop}, Volume: ${(volume * this.globalVolume).toFixed(4)})`);
        sound.play();

        if (!loop) {
            sound.onEnded = () => {
                //   console.log(`playPositionalSound: Non-looped sound "${name}" ended.`);
                if (soundObject.parent) {
                    soundObject.parent.remove(soundObject);
                }
                sound.disconnect();
            };
        }

        return soundObject;
    }

    stopLoopedSound(name) {
        const sound = this.loopedSounds.get(name);
        if (sound) {
            //   console.log(`stopLoopedSound: Attempting to stop sound "${name}". isPlaying: ${sound.isPlaying}`);
            if (sound.isPlaying) {
                sound.stop();
                //    console.log(`stopLoopedSound: Sound "${name}" stopped.`);
            } else {
                console.log(`stopLoopedSound: Sound "${name}" is not playing.`);
            }
        } else {
            console.warn(`stopLoopedSound: Sound "${name}" not found in tracked looped sounds.`);
        }
    }

    stopAllLoopedSounds() {
        console.log("stopAllLoopedSounds: Stopping all tracked looped sounds.");
        this.loopedSounds.forEach((sound, name) => {
            if (sound.isPlaying) {
                console.log(`stopAllLoopedSounds: Stopping sound "${name}".`);
                sound.stop();
            } else {
                console.log(`stopAllLoopedSounds: Sound "${name}" is already stopped.`);
            }
        });
        //  console.log("stopAllLoopedSounds: Finished attempting to stop sounds.");
    }

    stopAllSounds() {
        // console.log("stopAllSounds: Stopping all sounds.");
        this.stopAllLoopedSounds();
        // If you track all sounds (not just looped), add their stopping logic here
    }

    dispose() {
        console.log("AudioManager: Disposing.");
        this.disposeSettings();
        this.stopAllSounds();

        if (this.audioListener) {
            //  console.log("AudioManager: Closing audio context.");
            if (this.audioListener.context.state !== 'closed') {
                this.audioListener.context.close().catch(e => console.error("AudioManager: Error closing audio context:", e));
            }
            if (this.audioListener.parent) {
                //  console.log("AudioManager: Removing listener from parent.");
                this.audioListener.parent.remove(this.audioListener);
            }
            this.audioListener = null;
        }

        // console.log("AudioManager: Clearing sounds cache and looped sounds map.");
        this.sounds = {};
        this.loopedSounds.clear();

        //  console.log("AudioManager: Removing context resume listeners.");
        document.body.removeEventListener('click', this.resumeAudioContext);
        document.body.removeEventListener('touchstart', this.resumeAudioContext);
        document.body.removeEventListener('keydown', this.resumeAudioContext);

        console.log("AudioManager disposed.");
    }
}

export const AudioManagerInstance = AudioManager.getInstance();